/**
 * Techs data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. Owned by the techs feature. Shared entity reads (jobs, techs, customers,
 * vehicles, catalog) live in core/entities.ts.
 *
 * Endpoints (owners): profile/update/shifts/time off = P1 technicians; create/invite/deactivate = P1 users (invite SMS
 * via P4); statement = P6 `GET /v1/app/pay/techs/:id/statement`; KPIs = P7 analytics (`/v1/app/analytics/techs`).
 */
import { APP, ApiError, type Http } from '@app/core/http';
import { apiTech } from '@app/core/live/dto';
import type { Cents, ISO, Tech, UUID } from '@app/core/types';

/** `tech_shifts` row: day of week (0 = Sunday) + local wall-clock times in the tenant tz ("HH:MM"). */
export interface Shift { dow: number; start_local: string; end_local: string }
/** `tech_time_off` row (a tstzrange rendered as two instants). */
export interface TimeOff { id: UUID; starts_at: ISO; ends_at: ISO; reason: string | null }

/** GET /v1/app/techs/:id — the Tech row plus the profile fields the office edits. */
export interface TechProfile extends Tech {
  version?: number;
  license_no: string | null; license_state: string | null; license_expires: string | null;
  home_base_label: string | null; service_zones: string[];
  shifts: Shift[]; time_off: TimeOff[];
  invited_at: ISO | null; deactivated_at: ISO | null; deactivation_reason: string | null;
}

/** Create / patch body (snake_case as P1 stores it). Phone is E.164. */
export interface TechInput {
  first_name: string; last_name: string; phone_e164: string; email: string | null;
  skills: string[]; certifications: string[];
  license_no: string | null; license_state: string | null; license_expires: string | null;
  van_label: string | null; van_model: string | null;
  home_base_label: string | null; home_lat: number | null; home_lng: number | null; service_zones: string[];
  pay_type: Tech['pay_type']; pay_rate: number | null; max_jobs_per_day: number | null;
}

export interface InviteResult { status: 'queued' | 'sent'; to_e164: string; from_e164: string; link_expires_at: ISO | null }

export interface TechKpis {
  tech_id: UUID; from: string; to: string;
  jobs_completed: number; revenue_cents: Cents; avg_ticket_cents: Cents; on_time_pct: number | null;
  avg_rating: number | null; reviews: number; callbacks: number; hours_on_jobs: number; cancellations: number;
}

/** P6 tech statement row (pay.job_settlements joined with jobs + payouts). Read-only here: no settle actions. */
export interface StatementRow {
  job_id: UUID; job_no: string | null; direction: 'business_to_tech' | 'tech_to_business'; amount_cents: Cents;
  tech_payout_cents: Cents; cash_collected_cents: Cents; status: 'unsettled' | 'settled' | 'pending_cost';
  completed_at: ISO; settled_on: string | null; payout_id: UUID | null; batch_id: UUID | null; rail: string | null; reference: string | null;
}
export interface TechStatement {
  tech_id: UUID; tech_name: string; from: string; to: string; timezone: string;
  totals: {
    unsettled_commission_cents: Cents; unsettled_cash_held_cents: Cents; settled_commission_cents: Cents; settled_cash_cents: Cents;
    pending_cost_jobs: number; unsettled_net_due_cents: Cents;
  };
  current: { commission_cents: Cents; cash_held_cents: Cents; net_due_cents: Cents; jobs: number };
  rows: StatementRow[];
}

export interface Range { from?: string; to?: string }

export interface Api {
  profile(id: string): Promise<TechProfile>;
  create(body: TechInput, key: string): Promise<TechProfile>;
  update(id: string, patch: Partial<TechInput>, version: number | undefined): Promise<TechProfile>;
  setShifts(id: string, shifts: Shift[], version: number | undefined, key: string): Promise<Shift[]>;
  /** start/end are tenant-local "YYYY-MM-DDTHH:MM" (the server builds the tstzrange in the tenant tz). */
  addTimeOff(id: string, body: { start_local: string; end_local: string; reason: string | null }, key: string): Promise<TimeOff>;
  removeTimeOff(id: string, timeOffId: string): Promise<void>;
  invite(id: string, key: string): Promise<InviteResult>;
  deactivate(id: string, reason: string, key: string): Promise<TechProfile>;
  reactivate(id: string, key: string): Promise<TechProfile>;
  kpis(id: string, r: Range): Promise<TechKpis>;
  statement(id: string, r: Range): Promise<TechStatement>;
}

const T = (id: string) => `${APP}/techs/${encodeURIComponent(id)}`;
type Row = Record<string, any>;
/** P1 `/techs/:id` (technicians.getTechnician, top level) → TechProfile (Tech fields via the shared mapper). */
const profileOf = (r: Row): TechProfile => ({
  ...apiTech(r), version: r.version, license_no: r.license_no ?? null, license_state: r.license_state ?? null, license_expires: r.license_expires ?? null,
  home_base_label: r.home_base_label ?? null, service_zones: Array.isArray(r.service_zones) ? r.service_zones : [],
  shifts: Array.isArray(r.shifts) ? r.shifts.map((x: Row) => ({ dow: x.dow, start_local: x.start_local ?? x.start, end_local: x.end_local ?? x.end })) : [],
  time_off: Array.isArray(r.time_off) ? r.time_off.map((x: Row) => ({ id: x.id, starts_at: x.starts_at ?? x.start, ends_at: x.ends_at ?? x.end, reason: x.reason ?? null })) : [],
  invited_at: r.invited_at ?? null, deactivated_at: r.deactivated_at ?? null, deactivation_reason: r.deactivation_reason ?? null,
});
/** The profile fields P1 stores on ops.technicians (PATCH /techs/:id); names/phone/email live on the user. */
const PROFILE_KEYS = ['skills', 'certifications', 'van_label', 'pay_type', 'pay_rate', 'home_lat', 'home_lng', 'max_jobs_per_day', 'active'] as const;
const profilePatch = (v: Partial<TechInput> & { active?: boolean }) => Object.fromEntries(PROFILE_KEYS.filter((k) => (v as Row)[k] !== undefined).map((k) => [k, (v as Row)[k]]));
const USER_KEYS = ['first_name', 'last_name'] as const;
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);

/**
 * Live. Profile / shifts / time off = P1 `/techs/*` (tenants/technicians.js). Create + invite + deactivate = P1 users
 * (`POST /users/invite {role:'technician'}` creates the user + ops.technicians row and queues the SMS invite via P4;
 * `/users/:id/{resend_invite,disable,enable}`). KPIs = P7 `/analytics/techs?tech=`; statement = P6.
 */
export const live = (http: Http): Api => {
  const profile = (id: string) => http.get<Row>(T(id)).then(profileOf);
  const userOf = async (id: string) => { const p = await profile(id); if (!p.user_id) throw new ApiError(409, 'no_user_for_tech', null); return p; };
  return {
    profile,
    create: async (body, key) => {
      const r = await http.post<{ user: Row }>(`${APP}/users/invite`, {
        role: 'technician', phone: body.phone_e164, ...(body.email ? { email: body.email } : {}), first_name: body.first_name, last_name: body.last_name,
      }, { idempotencyKey: key });
      const techId = String(r.user?.tech_id ?? '');
      const patch = profilePatch(body);
      if (Object.keys(patch).length) await http.patch(T(techId), patch);
      return profile(techId);
    },
    update: async (id, patch, version) => {
      const names = Object.fromEntries(USER_KEYS.filter((k) => patch[k] !== undefined).map((k) => [k, patch[k]]));
      if (Object.keys(names).length || patch.phone_e164 !== undefined) {
        const p = await userOf(id);
        await http.patch(`${APP}/users/${encodeURIComponent(p.user_id!)}`, { ...names, ...(patch.phone_e164 !== undefined ? { phone: patch.phone_e164 } : {}) });
      }
      const prof = profilePatch(patch);
      return Object.keys(prof).length ? http.patch<Row>(T(id), prof, { version }).then(profileOf) : profile(id);
    },
    setShifts: (id, shifts, version, key) =>
      http.put<{ shifts: Shift[] } | Shift[]>(`${T(id)}/shifts`, { shifts }, { version, idempotencyKey: key }).then((r) => (Array.isArray(r) ? r : r.shifts)),
    addTimeOff: (id, body, key) => http.post<Row>(`${T(id)}/time_off`, body, { idempotencyKey: key })
      .then((r) => { const x = r.time_off ?? r; return { id: x.id, starts_at: x.starts_at ?? x.start, ends_at: x.ends_at ?? x.end, reason: x.reason ?? null }; }),
    removeTimeOff: (id, tid) => http.del(`${T(id)}/time_off/${encodeURIComponent(tid)}`),
    invite: async (id, key) => {
      const p = await userOf(id);
      const r = await http.post<{ invite?: Row } & Row>(`${APP}/users/${encodeURIComponent(p.user_id!)}/resend_invite`, {}, { idempotencyKey: key });
      const inv = r.invite ?? r;
      return { status: inv.queued ? 'queued' : 'sent', to_e164: p.phone_e164 ?? '', from_e164: '', link_expires_at: null };
    },
    deactivate: async (id, reason, key) => {
      const p = await userOf(id);
      await http.post(`${APP}/users/${encodeURIComponent(p.user_id!)}/disable`, { reason }, { idempotencyKey: key });
      await http.patch(T(id), { active: false });
      return profile(id);
    },
    reactivate: async (id, key) => {
      const p = await userOf(id);
      await http.post(`${APP}/users/${encodeURIComponent(p.user_id!)}/enable`, {}, { idempotencyKey: key });
      await http.patch(T(id), { active: true });
      return profile(id);
    },
    kpis: async (id, r) => {
      const from = r.from ?? daysAgo(30); const to = r.to ?? today();
      const x = await http.get<{ techs?: Row[] }>(`${APP}/analytics/techs`, { query: { from, to, tech: id } });
      const k = (x.techs ?? []).find((t) => t.tech_id === id) ?? {};
      const n = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);
      return {
        tech_id: id, from, to, jobs_completed: n(k.completed), revenue_cents: n(k.revenue_cents), avg_ticket_cents: n(k.avg_ticket_cents),
        on_time_pct: k.on_time_pct ?? null, avg_rating: k.rating ?? null, reviews: n(k.ratings), callbacks: 0, hours_on_jobs: 0, cancellations: n(k.cancelled),
      };
    },
    statement: (id, r) => http.get(`${APP}/pay/techs/${encodeURIComponent(id)}/statement`, { query: { ...r } }),
  };
};
